/**
 * Axios API client with JWT authentication and auto-refresh.
 *
 * Caching layers (in order of consultation):
 *   1. In-memory dedupe cache (5s) - prevents duplicate concurrent fires.
 *   2. Persistent SWR cache (IndexedDB) - returns cached responses across
 *      navigations and full reloads, then revalidates in the background.
 *      Only whitelisted GET endpoints (PERSISTENT_CACHE_RULES) are cached.
 *
 * Mutations (POST/PUT/PATCH/DELETE) clear both caches to keep data correct.
 */

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  buildCacheKey as buildPersistentCacheKey,
  classifyEntry as classifyPersistentEntry,
  clearAll as clearPersistentCache,
  deleteEntriesMatching as deletePersistentEntriesMatching,
  getAllEntries as getAllPersistentEntries,
  readEntry as readPersistentEntry,
  writeEntry as writePersistentEntry,
} from './persistentCache';

const RETRYABLE_HTTP_METHODS = new Set(['get', 'head', 'options']);
// 500 is included because a dropped RDS connection mid-request surfaces as a
// plain 500 (see api/db_resilience_middleware.py). Retries are already
// restricted to idempotent methods via RETRYABLE_HTTP_METHODS, so replaying
// a genuine 500 is harmless beyond the two extra attempts.
const RETRYABLE_STATUS_CODES = new Set([500, 502, 503, 504]);
const MAX_TRANSIENT_RETRIES = 2;
const DEFAULT_GET_CACHE_TTL_MS = 5000;
const PERSISTENT_CACHE_DEFAULT_GC_MS = 24 * 60 * 60 * 1000; // 24h hard ceiling
const AUTH_REFRESH_SKEW_SECONDS = 60;
const GET_NETWORK_CONCURRENCY_LIMIT = 5;
let activeQueuedGetRequests = 0;
const queuedGetRequests = [];

/**
 * Whitelist of GET endpoints safe to persist in the browser SWR cache.
 *
 * Each rule:
 *   - urlPrefix : path must start with this (after baseURL stripped).
 *   - ttlMs     : within this age -> serve from cache, no network call.
 *   - gcTimeMs  : within this age -> serve from cache + revalidate in BG.
 *
 * Endpoints with personally identifiable, security-sensitive, or rapidly
 * changing data (auth/profile, billing, notifications, admin) are
 * deliberately excluded.
 */
const PERSISTENT_CACHE_RULES = [
  { urlPrefix: '/flights/upcoming', ttlMs: 30 * 1000, gcTimeMs: 10 * 60 * 1000 },
  { urlPrefix: '/flights/statistics', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flights/slot-compliance', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flights/urgent-alerts', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/flights/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/aircraft/', ttlMs: 60 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  // The pilot roster drives live crew assignment and must never be restored
  // from a pre-mutation snapshot after a delete. Pilot documents/training
  // below remain cacheable, but /pilots/ itself always comes from the API.
  { urlPrefix: '/pilot-time-off-requests/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/pilot-time-off/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/pilot-documents/', ttlMs: 30 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/pilot-training/', ttlMs: 30 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  // 15 min TTL on the two heaviest dashboard statistics endpoints — matches
  // the bumped server-side Redis TTL. The SWR layer still triggers a silent
  // background revalidation on every visit; the bump only shortens the
  // network gap on cold-cache mounts.
  { urlPrefix: '/flight-logs/statistics', ttlMs: 15 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-logs/comprehensive-statistics', ttlMs: 15 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-logs/hours-summary', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-logs/yearly-forecast', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-logs/monthly-forecast-series', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-logs/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/aog-events/statistics', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/aog-events/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/reactive-maintenance/', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/tem/', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-checklists/tankering-savings-history', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/flight-checklists/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  // Contract intelligence state changes asynchronously (document processing,
  // AI analysis) and the Contratos page owns its own polling. A persisted
  // snapshot both hides fresh backend state and suppresses the polling
  // trigger, so /contracts/ deliberately always comes from the API.
  { urlPrefix: '/fixed-costs/', ttlMs: 2 * 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/variable-costs/', ttlMs: 2 * 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
  { urlPrefix: '/company-goals', ttlMs: 60 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/users/statistics', ttlMs: 15 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/passengers/', ttlMs: 30 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/passenger-ratings/', ttlMs: 5 * 60 * 1000, gcTimeMs: 60 * 60 * 1000 },
  { urlPrefix: '/airport-service-contacts/', ttlMs: 60 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/operations-docs/', ttlMs: 30 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/aircraft-documents/', ttlMs: 30 * 60 * 1000, gcTimeMs: 24 * 60 * 60 * 1000 },
  { urlPrefix: '/onboarding/status/', ttlMs: 60 * 1000, gcTimeMs: 30 * 60 * 1000 },
];

let refreshPromise = null;
const inflightGetRequests = new Map();
const getResponseCache = new Map();
const inflightPersistentRevalidations = new Map();
const recentPersistentInvalidations = [];
const PERSISTENT_INVALIDATION_GRACE_MS = 15000;

/**
 * Synchronous mirror of the persistent SWR cache.
 *
 * The IndexedDB-backed cache resolves asynchronously, which means a page
 * that needs cached data has to await at least one microtask before it
 * can render. That tick is enough to flash a loading spinner on every
 * navigation, which defeats the "feels instant" UX goal of the SWR
 * layer. The sync mirror lets pages read last-known responses as
 * `useState` initial values - i.e. *before* the first render - so a
 * cache hit paints stale data instantly and the awaiting fetch then
 * silently refreshes it.
 *
 * Two-tier design:
 *   - In-memory `Map` (this file) is the read path: O(1) sync lookup.
 *   - localStorage backing (prefix below) survives full page reloads
 *     and new tabs. Without this, a hard refresh (Cmd-R) would empty
 *     the in-memory tier and the *first* page after the reload would
 *     pay the full loading-spinner cost while the async IndexedDB
 *     hydration races the first render. With localStorage backing the
 *     boot hydration is synchronous and the very first render after a
 *     reload already has the data.
 *
 * Invariants:
 *   - Keyed by the full persistent cache key (includes user scope).
 *   - Holds *only* the response payload (`response.data`), not the
 *     full axios envelope, so accidental retention of large headers/
 *     config objects is avoided.
 *   - Populated on (a) every successful network response that matches a
 *     persistent rule, (b) every IndexedDB cache hit, and (c) the
 *     synchronous boot hydration from localStorage below.
 *   - Drained by `_clearPersistentCacheAsync` (logout / token rotation /
 *     API base URL change) and by `_invalidatePersistentForMutation`
 *     (matching prefix scopes for the current user).
 *
 * Storage budget:
 *   - SYNC_MIRROR_LS_PREFIX scopes the keys so we never collide with
 *     auth tokens or the persistentCache fallback.
 *   - SYNC_MIRROR_MAX_BYTES_PER_ENTRY skips entries larger than ~200KB
 *     so a 600KB flight-list payload doesn't blow through the LS quota
 *     by itself. Larger payloads still live in IndexedDB (slow path).
 *   - SYNC_MIRROR_MAX_ENTRIES caps the total number of cached entries.
 *     On overflow we LRU-evict (oldest by last-access timestamp first).
 */
const _syncMirror = new Map();
const _syncMirrorLruTimestamps = new Map();
const SYNC_MIRROR_LS_PREFIX = 'aviatto:syncMirror:';
// Per-entry cap. The cap exists so a single pathological response can't
// fill the entire LS quota by itself; combined with the total-budget
// check below, it keeps storage bounded under all conditions.
//
// 3MB was chosen empirically: FlightAgenda's main monthly payload for a
// busy month can still exceed 1.5MB once checklist/slot metadata is attached.
// The previous 1.5MB cap dropped this entry, which meant every hard reload
// of FlightAgenda fell back to the slow path even though smaller pages
// (Dashboard) were instant.
//
// The browser quota for localStorage is typically 5-10MB per origin, so
// 3MB per entry leaves enough headroom to coexist with the rest of the
// app's LS usage (auth tokens, mission control state, FRAT drafts...).
const SYNC_MIRROR_MAX_BYTES_PER_ENTRY = 3 * 1024 * 1024;
const SYNC_MIRROR_MAX_ENTRIES = 80;
// Total LS budget for the sync mirror. Keep this below the usual 5MB
// browser quota so auth/session writes always have breathing room even
// after a large Agenda payload is cached.
const SYNC_MIRROR_MAX_TOTAL_BYTES = 3 * 1024 * 1024;

function _syncMirrorPersistToLS(key, data) {
  try {
    if (typeof localStorage === 'undefined') return;
    let serialized;
    try {
      serialized = JSON.stringify(data);
    } catch {
      // Non-serializable payload (e.g. circular ref). Skip LS write but
      // keep the in-memory mirror so in-session navigations still hit.
      return;
    }
    if (!serialized) return;
    if (serialized.length * 2 > SYNC_MIRROR_MAX_BYTES_PER_ENTRY) {
      // Too big - drop any existing LS copy so we don't serve a stale
      // payload smaller than the latest one.
      try { localStorage.removeItem(SYNC_MIRROR_LS_PREFIX + key); } catch { /* noop */ }
      return;
    }
    try {
      localStorage.setItem(SYNC_MIRROR_LS_PREFIX + key, serialized);
    } catch {
      // Quota error or storage disabled. Try to evict and retry once.
      _syncMirrorEvictOverflow(true);
      try {
        localStorage.setItem(SYNC_MIRROR_LS_PREFIX + key, serialized);
      } catch {
        // Give up - in-memory mirror still has the data.
      }
    }
  } catch {
    // ignore
  }
}

function _syncMirrorRemoveFromLS(key) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(SYNC_MIRROR_LS_PREFIX + key);
  } catch {
    // ignore
  }
}

/**
 * Compute total bytes used by the sync-mirror LS prefix. Cheap enough
 * to call on every write because we already pay one localStorage.length
 * iteration there. Returns approximate UTF-16 size (length * 2).
 */
function _syncMirrorTotalLSBytes() {
  let total = 0;
  try {
    if (typeof localStorage === 'undefined') return 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(SYNC_MIRROR_LS_PREFIX)) continue;
      const v = localStorage.getItem(k);
      total += (v?.length || 0) * 2;
    }
  } catch {
    // ignore
  }
  return total;
}

function _syncMirrorEvictOverflow(forceAggressive = false) {
  try {
    const overEntryCap = _syncMirror.size > SYNC_MIRROR_MAX_ENTRIES;
    const overByteCap = _syncMirrorTotalLSBytes() > SYNC_MIRROR_MAX_TOTAL_BYTES;
    if (!overEntryCap && !overByteCap && !forceAggressive) return;

    const targetSize = forceAggressive
      ? Math.floor(SYNC_MIRROR_MAX_ENTRIES * 0.5)
      : Math.floor(SYNC_MIRROR_MAX_ENTRIES * 0.85);
    const targetBytes = forceAggressive
      ? Math.floor(SYNC_MIRROR_MAX_TOTAL_BYTES * 0.5)
      : Math.floor(SYNC_MIRROR_MAX_TOTAL_BYTES * 0.85);

    const sortedByAge = Array.from(_syncMirrorLruTimestamps.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([k]) => k);

    while (sortedByAge.length > 0) {
      const tooManyEntries = _syncMirror.size > targetSize;
      const tooManyBytes = _syncMirrorTotalLSBytes() > targetBytes;
      if (!tooManyEntries && !tooManyBytes) break;
      const k = sortedByAge.shift();
      _syncMirror.delete(k);
      _syncMirrorLruTimestamps.delete(k);
      _syncMirrorRemoveFromLS(k);
    }
  } catch {
    // ignore
  }
}

function _syncMirrorSet(key, data) {
  if (!key) return;
  try {
    const k = String(key);
    _syncMirror.set(k, data);
    _syncMirrorLruTimestamps.set(k, Date.now());
    _syncMirrorPersistToLS(k, data);
    _syncMirrorEvictOverflow(false);
  } catch {
    // ignore
  }
}

function _syncMirrorGet(key) {
  if (!key) return undefined;
  try {
    const k = String(key);
    const value = _syncMirror.get(k);
    if (value !== undefined) {
      _syncMirrorLruTimestamps.set(k, Date.now());
    }
    return value;
  } catch {
    return undefined;
  }
}

function _syncMirrorDeleteMatching(predicate) {
  try {
    if (typeof predicate !== 'function') {
      _syncMirror.clear();
      _syncMirrorLruTimestamps.clear();
      // Drop every prefixed LS entry too - mutation invalidation must
      // never leave a synchronous read returning pre-mutation data.
      try {
        if (typeof localStorage !== 'undefined') {
          const toRemove = [];
          for (let i = 0; i < localStorage.length; i += 1) {
            const lsKey = localStorage.key(i);
            if (lsKey && lsKey.startsWith(SYNC_MIRROR_LS_PREFIX)) toRemove.push(lsKey);
          }
          toRemove.forEach((k) => {
            try { localStorage.removeItem(k); } catch { /* noop */ }
          });
        }
      } catch {
        // ignore
      }
      return;
    }
    const toDrop = [];
    for (const key of _syncMirror.keys()) {
      try {
        if (predicate(String(key))) toDrop.push(key);
      } catch {
        // ignore broken predicate evaluation for this key
      }
    }
    toDrop.forEach((k) => {
      _syncMirror.delete(k);
      _syncMirrorLruTimestamps.delete(k);
      _syncMirrorRemoveFromLS(k);
    });
  } catch {
    // ignore
  }
}

function _syncMirrorClear() {
  _syncMirrorDeleteMatching(undefined);
}

/**
 * Synchronous boot hydration from localStorage. Runs once on module
 * load BEFORE any useState initialiser fires, so the very first render
 * after a hard reload can paint cached data immediately.
 *
 * Skips entries that fail to parse rather than throwing - a corrupt
 * envelope just misses the fast path on this load and gets overwritten
 * the next time the same endpoint is requested.
 */
function _syncMirrorBootFromLS() {
  try {
    if (typeof localStorage === 'undefined') return;
    for (let i = 0; i < localStorage.length; i += 1) {
      const lsKey = localStorage.key(i);
      if (!lsKey || !lsKey.startsWith(SYNC_MIRROR_LS_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(lsKey);
        if (!raw) continue;
        const data = JSON.parse(raw);
        const cacheKey = lsKey.slice(SYNC_MIRROR_LS_PREFIX.length);
        _syncMirror.set(cacheKey, data);
        _syncMirrorLruTimestamps.set(cacheKey, Date.now());
      } catch {
        // skip corrupt entries
      }
    }
  } catch {
    // ignore
  }
}

// Synchronously seed the in-memory mirror from localStorage at module
// load time. This happens BEFORE any React component code runs, so the
// very first useState(() => apiClient.getCachedDataSync(...)) fires
// against a populated mirror.
_syncMirrorBootFromLS();

function _sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function _tokenNeedsRefresh(token, skewSeconds = AUTH_REFRESH_SKEW_SECONDS) {
  try {
    const decoded = jwtDecode(token);
    const exp = Number(decoded?.exp || 0);
    if (!exp) return false;
    return exp <= (Date.now() / 1000) + skewSeconds;
  } catch {
    return false;
  }
}

function _isAuthLifecycleUrl(url = '') {
  const value = String(url || '');
  return value.includes('/auth/login/') || value.includes('/auth/refresh/');
}

function _currentAuthenticatedSessionIdentity() {
  let token = '';
  try {
    token = localStorage.getItem('access_token') || '';
  } catch {
    return '';
  }
  if (!token) return '';

  try {
    const decoded = jwtDecode(token);
    const parts = _scopePartsFromIdentity(decoded);
    if (parts.length) {
      return parts.join('|');
    }
  } catch {
    // Invalid tokens are rejected by the backend. Keep a stable local marker
    // so a different opaque token still cannot inherit an in-flight response.
  }

  // Do not place the token itself on axios config objects, which are commonly
  // logged by error reporters. This lightweight hash is only a change marker,
  // not a cryptographic identity or authorization decision.
  let hash = 2166136261;
  for (let index = 0; index < token.length; index += 1) {
    hash ^= token.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `opaque:${(hash >>> 0).toString(16)}`;
}

function _newSessionChangedError() {
  return Object.assign(
    new Error('Authenticated session changed while the request was in progress'),
    { code: 'AVIATTO_SESSION_CHANGED' }
  );
}

function _isSessionChangedError(error) {
  return error?.code === 'AVIATTO_SESSION_CHANGED';
}

function _stampRequestSession(config = {}) {
  if (
    config.__skipSessionGuard ||
    config.__skipAuthHeader ||
    _isAuthLifecycleUrl(config.url)
  ) {
    return config;
  }
  if (config.__aviattoSessionIdentity === undefined) {
    config.__aviattoSessionIdentity = _currentAuthenticatedSessionIdentity();
  }
  return config;
}

function _assertRequestSessionUnchanged(config = {}) {
  const expectedIdentity = config.__aviattoSessionIdentity;
  if (!expectedIdentity) return;
  if (_currentAuthenticatedSessionIdentity() !== expectedIdentity) {
    throw _newSessionChangedError();
  }
}

const TENANT_ECHO_RESPONSE_HEADER = 'x-aviatto-tenant-schema';

function _newTenantMismatchError(requestedTenant, servedTenant) {
  return Object.assign(
    new Error('Response was served by a different tenant schema than the session requested'),
    {
      code: 'AVIATTO_TENANT_MISMATCH',
      requestedTenant,
      servedTenant,
    }
  );
}

/**
 * Fail-closed tenant echo verification (incident 2026-07-25).
 *
 * The request interceptor stamps every authenticated call with the JWT's
 * tenant via the `X-Aviatto-Tenant-Schema` request header, and the backend
 * middleware echoes the schema that ACTUALLY served the request on the
 * response. If the two ever disagree — a server-side misroute, a poisoned
 * intermediary, or any future routing regression — the payload must never
 * be rendered or cached under this session. Rejecting here (before the
 * shared/persistent cache `.then` handlers run) guarantees both.
 *
 * The check is skipped when either side is absent: requests sent without a
 * tenant-bound token (anonymous, auth lifecycle) and responses from
 * backends that don't emit the echo (older builds, single-schema dev).
 */
function _assertResponseTenantMatches(response = {}) {
  const config = response?.config || {};
  if (
    config.__skipTenantEchoCheck ||
    config.__skipAuthHeader ||
    _isAuthLifecycleUrl(String(config.url || ''))
  ) {
    return;
  }
  const requestHeaders = config.headers || {};
  const requestedTenant = String(
    requestHeaders['X-Aviatto-Tenant-Schema'] || requestHeaders[TENANT_ECHO_RESPONSE_HEADER] || ''
  ).trim();
  if (!requestedTenant) return;

  const responseHeaders = response?.headers || {};
  const servedTenant = String(
    responseHeaders[TENANT_ECHO_RESPONSE_HEADER] || responseHeaders['X-Aviatto-Tenant-Schema'] || ''
  ).trim();
  if (!servedTenant || servedTenant === requestedTenant) return;

  // Redacted security telemetry: route + tenants only — never tokens,
  // never response payloads. The DOM event lets monitoring hooks forward
  // the signal without this module knowing about them.
  const securityEvent = {
    code: 'AVIATTO_TENANT_MISMATCH',
    path: String(config.url || '').split('?')[0],
    method: String(config.method || 'get').toLowerCase(),
    requestedTenant,
    servedTenant,
    at: new Date().toISOString(),
  };
  try {
    console.error('[apiClient] Tenant echo mismatch — response dropped.', securityEvent);
  } catch {
    // logging must never mask the rejection below
  }
  try {
    window.dispatchEvent(new CustomEvent('aviatto:security-event', { detail: securityEvent }));
  } catch {
    // non-browser callers do not need the DOM event
  }
  throw _newTenantMismatchError(requestedTenant, servedTenant);
}

function _shouldQueueNetworkRequest(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  return method === 'get' && !config.__skipNetworkQueue;
}

function _drainQueuedGetRequests() {
  while (activeQueuedGetRequests < GET_NETWORK_CONCURRENCY_LIMIT && queuedGetRequests.length > 0) {
    const next = queuedGetRequests.shift();
    if (!next) break;
    activeQueuedGetRequests += 1;
    rawApiRequest(next.config)
      .then(next.resolve)
      .catch(next.reject)
      .finally(() => {
        activeQueuedGetRequests = Math.max(0, activeQueuedGetRequests - 1);
        _drainQueuedGetRequests();
      });
  }
}

function queuedRawApiRequest(config = {}) {
  if (!_shouldQueueNetworkRequest(config)) {
    return rawApiRequest(config);
  }
  return new Promise((resolve, reject) => {
    queuedGetRequests.push({ config, resolve, reject });
    _drainQueuedGetRequests();
  });
}

function _normalizeUrlPath(url) {
  try {
    return String(url || '').trim();
  } catch {
    return '';
  }
}

function _stableSerialize(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return `[${value.map((item) => _stableSerialize(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${key}:${_stableSerialize(value[key])}`).join(',')}}`;
  }
  return String(value);
}

function _buildRequestCacheKey(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  const baseURL = String(config.baseURL || API_BASE_URL || '');
  const url = _normalizeUrlPath(config.url);
  const params = _stableSerialize(config.params || {});
  const userScope = _currentUserScopeForCache();
  return `${userScope}|${method}|${baseURL}|${url}|${params}`;
}

function _shouldUseSharedGetCache(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  if (!RETRYABLE_HTTP_METHODS.has(method)) return false;
  if (config.__skipSharedCache) return false;
  return true;
}

function _cloneResponseLike(response) {
  if (!response || typeof response !== 'object') return response;
  return {
    ...response,
    headers: response.headers ? { ...response.headers } : response.headers,
    config: response.config ? { ...response.config } : response.config,
    data: response.data,
  };
}

function _clearSharedGetCache() {
  inflightGetRequests.clear();
  getResponseCache.clear();
}

/**
 * Drop everything we've persisted in IndexedDB / localStorage *and* the
 * in-memory sync mirror so future synchronous reads can't return data
 * belonging to a previous user/tenant. Fire-and-forget; never blocks
 * the caller.
 */
function _clearPersistentCacheAsync() {
  _markPersistentInvalidation(() => true);
  _syncMirrorClear();
  try {
    clearPersistentCache().catch(() => { /* noop */ });
  } catch {
    // ignore
  }
}

function _normalizePathForRules(url = '') {
  try {
    const raw = String(url || '').trim();
    if (!raw) return '';
    // Strip query string for prefix matching; cache key already includes params.
    const noQuery = raw.split('?')[0];
    // If absolute URL was passed, strip origin so rules can match path only.
    if (/^https?:\/\//i.test(noQuery)) {
      try {
        const parsed = new URL(noQuery);
        return parsed.pathname.replace(/^\/api(?=\/)/, '');
      } catch {
        return noQuery;
      }
    }
    return noQuery;
  } catch {
    return '';
  }
}

function _matchPersistentRule(url) {
  const path = _normalizePathForRules(url);
  if (!path) return null;
  for (const rule of PERSISTENT_CACHE_RULES) {
    if (path.startsWith(rule.urlPrefix)) {
      return rule;
    }
  }
  return null;
}

function _decodeStoredAccessToken() {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    return jwtDecode(token);
  } catch {
    return null;
  }
}

function _storedProfileMatchesToken(profile, decoded) {
  if (!profile || typeof profile !== 'object' || !decoded) return false;

  const tokenUserId = decoded?.user_id || decoded?.id || decoded?.sub || '';
  const profileUserId = profile?.id || profile?.user_id || '';
  if (tokenUserId && profileUserId && String(tokenUserId) !== String(profileUserId)) {
    return false;
  }

  const tokenTenantSchema = String(decoded?.tenant_schema || decoded?.schema_name || '').trim();
  const profileTenantSchema = String(profile?.tenant_schema || profile?.schema_name || '').trim();
  if (tokenTenantSchema && profileTenantSchema !== tokenTenantSchema) {
    return false;
  }

  const tokenUsername = String(decoded?.username || '').trim().toLowerCase();
  const profileUsername = String(profile?.username || '').trim().toLowerCase();
  if (tokenUsername && profileUsername && tokenUsername !== profileUsername) {
    return false;
  }

  const tokenEmail = String(decoded?.email || '').trim().toLowerCase();
  const profileEmail = String(profile?.email || '').trim().toLowerCase();
  if (tokenEmail && profileEmail && tokenEmail !== profileEmail) {
    return false;
  }

  return true;
}

function _scopePartsFromIdentity(identity = {}, prefix = '') {
  const parts = [];
  const userId = identity?.user_id || identity?.id || identity?.sub || identity?.username || '';
  const tenantSchema = identity?.tenant_schema || identity?.schema_name || '';
  const tenant = identity?.tenant_id || identity?.company_id || identity?.company || '';
  if (tenantSchema) parts.push(`${prefix}schema:${tenantSchema}`);
  if (tenant) parts.push(`${prefix}tenant:${tenant}`);
  if (userId) parts.push(`${prefix}user:${userId}`);
  return parts;
}

// Fail-closed cache scoping (incident 2026-07-25): numeric user and company
// IDs repeat across tenant schemas, so a scope like `tenant:1|user:1` built
// from a session WITHOUT a `tenant_schema` claim can collide with a different
// tenant's identically numbered session. Such legacy/dev sessions get this
// per-boot random salt prefixed to their scope: caching still works within
// the current page lifetime, but the entries can never be read after a
// reload, by another tab boot, or by a post-switch identity.
const _NO_SCHEMA_SCOPE_EPOCH = `noschema:${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

function _currentUserScopeForCache() {
  const scopeParts = [];
  const decodedAccess = _decodeStoredAccessToken();
  if (decodedAccess) {
    scopeParts.push(..._scopePartsFromIdentity(decodedAccess));
  }

  try {
    const userRaw = localStorage.getItem('user');
    if (!decodedAccess && userRaw) {
      const parsed = JSON.parse(userRaw);
      scopeParts.push(..._scopePartsFromIdentity(parsed));
    }
  } catch {
    // fall through
  }
  try {
    const profileRaw = localStorage.getItem('user_profile');
    if (profileRaw) {
      const profile = JSON.parse(profileRaw);
      const canUseProfile = decodedAccess
        ? _storedProfileMatchesToken(profile, decodedAccess)
        : true;
      if (canUseProfile) {
        scopeParts.push(..._scopePartsFromIdentity(profile, 'profile'));
      }
    }
  } catch {
    // fall through
  }
  if (scopeParts.length) {
    const joined = scopeParts.join('|');
    // The access token is the authority: when it exists but carries no
    // tenant_schema claim, the scope is collision-prone and must be salted
    // even if a stored profile contributed a schema part. Without a token,
    // salt whenever no source contributed any schema segment at all.
    const saltRequired = decodedAccess
      ? !(decodedAccess?.tenant_schema || decodedAccess?.schema_name)
      : !scopeParts.some((part) => part.includes('schema:'));
    return saltRequired ? `${_NO_SCHEMA_SCOPE_EPOCH}|${joined}` : joined;
  }
  try {
    const apiBase = localStorage.getItem('aviatto_api_base_url') || '';
    return `anon|${apiBase}`;
  } catch {
    return 'anon';
  }
}

function _buildPersistentCacheKey(config = {}) {
  return buildPersistentCacheKey({
    userScope: _currentUserScopeForCache(),
    method: 'get',
    baseURL: String(config.baseURL || API_BASE_URL || ''),
    url: _normalizePathForRules(config.url),
    params: config.params || {},
  });
}

/**
 * Build a small response shape that matches axios responses closely enough
 * for downstream callers (which mostly read .data).
 */
function _buildSyntheticResponse(config, data) {
  return {
    data,
    status: 200,
    statusText: 'OK (cached)',
    headers: { 'x-aviatto-cache': 'hit' },
    config: { ...config },
    request: null,
  };
}

/**
 * Decide whether persistent cache may be consulted/written for a request.
 * Conservative: GET only, not opted-out, and matched by a rule.
 */
function _persistentRuleFor(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get') return null;
  if (config.__skipPersistentCache) return null;
  return _matchPersistentRule(config.url);
}

function _pruneRecentPersistentInvalidations(now = Date.now()) {
  for (let i = recentPersistentInvalidations.length - 1; i >= 0; i -= 1) {
    if (recentPersistentInvalidations[i].expiresAt <= now) {
      recentPersistentInvalidations.splice(i, 1);
    }
  }
}

function _markPersistentInvalidation(predicate) {
  if (typeof predicate !== 'function') return;
  const now = Date.now();
  _pruneRecentPersistentInvalidations(now);
  recentPersistentInvalidations.push({
    predicate,
    createdAt: now,
    expiresAt: now + PERSISTENT_INVALIDATION_GRACE_MS,
  });
}

function _latestPersistentInvalidationAtForKey(key) {
  if (!key) return 0;
  const now = Date.now();
  _pruneRecentPersistentInvalidations(now);
  let latest = 0;
  for (const invalidation of recentPersistentInvalidations) {
    try {
      if (invalidation.predicate(String(key))) {
        latest = Math.max(latest, invalidation.createdAt || 0);
      }
    } catch {
      // Ignore broken invalidation predicates; the async deletion still runs.
    }
  }
  return latest;
}

function _kickPersistentRevalidation(config, cacheKey, rule) {
  if (inflightPersistentRevalidations.has(cacheKey)) return;
  const requestStartedAt = Date.now();
  const promise = queuedRawApiRequest({ ...config, __skipPersistentCache: true, __skipSharedCache: true })
    .then(async (response) => {
      if (_latestPersistentInvalidationAtForKey(cacheKey) > requestStartedAt) {
        return;
      }
      try {
        await writePersistentEntry(cacheKey, {
          data: response?.data,
          ttlMs: rule.ttlMs,
          gcTimeMs: rule.gcTimeMs || PERSISTENT_CACHE_DEFAULT_GC_MS,
        });
      } catch {
        // ignore persistence errors
      }
      // Keep the sync mirror current so subsequent navigations render
      // the freshly revalidated payload immediately.
      _syncMirrorSet(cacheKey, response?.data);
    })
    .catch(() => {
      // Background revalidation failures are silent; the next foreground
      // request will surface the error normally.
    })
    .finally(() => {
      inflightPersistentRevalidations.delete(cacheKey);
    });
  inflightPersistentRevalidations.set(cacheKey, promise);
}

/**
 * Invalidate persistent cache entries that could be impacted by a write
 * to the given URL. We invalidate by URL prefix scope rather than the
 * exact key so that, e.g., creating a flight clears every cached flight
 * list/range query for the same user.
 */
function _invalidatePersistentForMutation(config = {}) {
  const path = _normalizePathForRules(config?.url || '');
  if (!path) {
    _clearPersistentCacheAsync();
    return;
  }
  const scopes = new Set();
  // Map mutation endpoints to one or more cache prefixes that should be
  // dropped. Conservative on purpose: better to refetch than show stale.
  const addScope = (prefix) => scopes.add(`|${prefix}`);
  if (path.startsWith('/flights')) {
    addScope('/flights/');
    addScope('/flight-logs/');
    addScope('/aog-events/');
    addScope('/flight-checklists/');
    addScope('/tem/');
    addScope('/users/statistics');
  } else if (path.startsWith('/flight-logs')) {
    addScope('/flight-logs/');
    addScope('/users/statistics');
    addScope('/tem/');
  } else if (path.startsWith('/flight-checklists')) {
    addScope('/flight-checklists/');
    addScope('/tem/');
  } else if (path.startsWith('/safety-audit-reports')) {
    addScope('/safety-audit-reports/');
  } else if (path.startsWith('/aog-events')) {
    addScope('/aog-events/');
    addScope('/reactive-maintenance/');
  } else if (path.startsWith('/reactive-maintenance')) {
    addScope('/reactive-maintenance/');
  } else if (path.startsWith('/fixed-costs')) {
    addScope('/fixed-costs/');
    addScope('/users/statistics');
  } else if (path.startsWith('/variable-costs')) {
    addScope('/variable-costs/');
    addScope('/flight-logs/');
    addScope('/users/statistics');
  } else if (path.startsWith('/aircraft')) {
    addScope('/aircraft/');
  } else if (path.startsWith('/pilots') || path.startsWith('/pilot-')) {
    addScope('/pilots/');
    addScope('/pilot-time-off-requests/');
    addScope('/pilot-time-off/');
    addScope('/pilot-documents/');
    addScope('/pilot-training/');
  } else if (path.startsWith('/passengers') || path.startsWith('/passenger-')) {
    addScope('/passengers/');
    addScope('/passenger-ratings/');
  } else if (path.startsWith('/operations-docs')) {
    addScope('/operations-docs/');
  } else if (path.startsWith('/airport-service-contacts')) {
    addScope('/airport-service-contacts/');
  } else if (path.startsWith('/aircraft-documents')) {
    addScope('/aircraft-documents/');
  }

  if (scopes.size === 0) {
    // Unknown mutation - safest is to drop everything for this user.
    _clearPersistentCacheAsync();
    return;
  }
  const userScope = _currentUserScopeForCache();
  const matchesUserAndScope = (key) => {
    if (!key.includes(`|${userScope}|`)) return false;
    for (const scope of scopes) {
      if (key.includes(scope)) return true;
    }
    return false;
  };
  // Drop matching entries from BOTH the persistent store AND the
  // synchronous in-memory mirror so the next render cannot serve a
  // pre-mutation snapshot from the sync fast-path.
  _markPersistentInvalidation(matchesUserAndScope);
  _syncMirrorDeleteMatching(matchesUserAndScope);
  try {
    deletePersistentEntriesMatching(matchesUserAndScope).catch(() => { /* noop */ });
  } catch {
    // ignore
  }
}

function _clearAuthStorage() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('aviatto_local_session');
  } catch {
    // Ignore storage failures.
  }
  _clearPersistentCacheAsync();
}

/**
 * Bounce an unauthenticated session to /login, remembering where the operator
 * was so re-authentication returns them there.
 *
 * This is a hard navigation, so react-router's `location.state.from` (which
 * `Login.finishLogin` also honors) is lost. Without the `redirect` query the
 * login screen falls back to `resolvePostLoginPath()` — Mission Control — and
 * the operator silently lands on the wrong page. That is what made "clicking
 * Painel Safety opens Mission Control" look like a routing bug: /tem fires
 * several safety endpoints on mount, so it is one of the pages most likely to
 * trip a 401 mid-render.
 */
function _redirectToLogin() {
  try {
    if (window.location.pathname === '/login') return;
    const here = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
    // Only same-origin relative paths; `//host` would navigate off-site.
    const safe = here.startsWith('/') && !here.startsWith('//') ? here : '';
    window.location.href = safe
      ? `/login?redirect=${encodeURIComponent(safe)}`
      : '/login';
  } catch {
    // Ignore navigation failures.
  }
}

function _isLocalSession() {
  try {
    return localStorage.getItem('aviatto_local_session') === 'true';
  } catch {
    return false;
  }
}

const LOCAL_BACKEND_API = 'http://127.0.0.1:8000/api';

// A refresh failure is only "definitive" (⇒ log the user out) when the refresh
// endpoint itself rejects the token: HTTP 401/400, or there is simply no
// refresh token to use. Everything else (network error, timeout, 5xx during a
// rolling deploy, transient rotation write failure) is treated as retryable so
// a momentary blip does NOT bounce an otherwise-valid session to /login.
function _isDefinitiveAuthFailure(error) {
  if (error?.isAuthRejection) return true;
  const status = error?.response?.status;
  return status === 401 || status === 403 || status === 400;
}

async function _refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw Object.assign(new Error('Missing refresh token'), { isAuthRejection: true });
  }

  const refreshUrl = _isLocalSession()
    ? `${LOCAL_BACKEND_API}/auth/refresh/`
    : `${API_BASE_URL}/auth/refresh/`;

  const { data } = await axios.post(
    refreshUrl,
    { refresh: refreshToken },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    }
  );

  const nextAccessToken = data?.access;
  if (!nextAccessToken) {
    throw new Error('Refresh response missing access token');
  }

  // A user can log out or switch accounts while the refresh request is on the
  // wire. Never let the old account's late response overwrite the replacement
  // session. Callers treat this as a safe cancellation, not an auth rejection.
  if (localStorage.getItem('refresh_token') !== refreshToken) {
    throw _newSessionChangedError();
  }

  localStorage.setItem('access_token', nextAccessToken);
  if (data?.refresh) {
    localStorage.setItem('refresh_token', data.refresh);
  }
  try {
    const decoded = jwtDecode(nextAccessToken);
    if (decoded) {
      localStorage.setItem('user', JSON.stringify(decoded));
      const profileRaw = localStorage.getItem('user_profile');
      if (profileRaw) {
        const profile = JSON.parse(profileRaw);
        if (!_storedProfileMatchesToken(profile, decoded)) {
          localStorage.removeItem('user_profile');
        }
      }
    }
  } catch {
    // Token storage already succeeded; profile/cache scope will fall back safely.
  }

  return nextAccessToken;
}

async function _ensureFreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = _refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function _shouldRetryTransientRequest(error) {
  const request = error?.config;
  if (!request || request.__skipRetry) return false;

  const method = String(request.method || 'get').toLowerCase();
  if (!RETRYABLE_HTTP_METHODS.has(method)) return false;

  const status = error?.response?.status;
  if (status && RETRYABLE_STATUS_CODES.has(status)) return true;

  if (!error?.response) {
    const code = String(error?.code || '').toUpperCase();
    const message = String(error?.message || '').toLowerCase();
    return code === 'ECONNABORTED' || message.includes('timeout') || message.includes('network error');
  }

  return false;
}

function _isLocalhostUrl(url) {
  try {
    return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(url || '');
  } catch {
    return false;
  }
}

function _isRunningOnLocalhost() {
  try {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1';
  } catch {
    return false;
  }
}

function _normalizeLocalhostToIPv4(url) {
  // macOS often resolves "localhost" to ::1; Django runserver typically binds only to 127.0.0.1.
  // If the frontend uses an explicit localhost API URL, rewrite it to 127.0.0.1 to avoid
  // ERR_CONNECTION_REFUSED on IPv6.
  try {
    if (!url || typeof url !== 'string') return url;
    return url.replace(/^https?:\/\/localhost\b/i, (m) => m.replace(/localhost/i, '127.0.0.1'));
  } catch {
    return url;
  }
}

function _stripApiSuffix(url) {
  try {
    if (!url || typeof url !== 'string') return url;
    return url.replace(/\/api\/?$/i, '');
  } catch {
    return url;
  }
}

// API base URL resolution:
// 1. Prefer explicit env var (VITE_API_BASE_URL)
// 2. In dev, default to '/api' (Vite proxy)
// 3. In prod with subdomains, derive from VITE_API_BACKEND_HOST
//    e.g. "acme.aviatto.com.br" → "https://acme.aviatto.com.br/api"
const ENV_API = _normalizeLocalhostToIPv4(import.meta.env.VITE_API_BASE_URL);
const SAFE_ENV_API = (!_isRunningOnLocalhost() && _isLocalhostUrl(ENV_API)) ? null : ENV_API;
const DEV_LOCALHOST = import.meta.env.DEV && _isRunningOnLocalhost();
const ALLOW_REMOTE_API_IN_DEV = String(import.meta.env.VITE_ALLOW_REMOTE_API_IN_DEV || '').toLowerCase() === 'true';
const DEV_PROXY_API = DEV_LOCALHOST ? (ALLOW_REMOTE_API_IN_DEV ? (SAFE_ENV_API || ENV_API || null) : null) : null;
function _resolveProductionApi() {
  // If a backend host template is set, use the current hostname (subdomain-aware).
  const backendHost = import.meta.env.VITE_API_BACKEND_HOST;
  if (backendHost) {
    return `https://${backendHost}/api`;
  }
  // Legacy: same-origin requests when frontend and backend share the same domain/subdomain.
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('.aviatto.com.br')) {
    return `${window.location.origin}/api`;
  }
  return 'https://api.zulbot.com/api';
}

export const API_BASE_URL =
  DEV_LOCALHOST
    ? '/api'
    : (SAFE_ENV_API || (import.meta.env.DEV ? '/api' : _resolveProductionApi()));

// Backend root (used for file links and non-API endpoints).
export const BACKEND_ROOT_URL = DEV_LOCALHOST
  ? _stripApiSuffix(DEV_PROXY_API || ENV_API || 'http://127.0.0.1:8000/api')
  : _stripApiSuffix(API_BASE_URL);

// If the API base URL changes (e.g., switching from localhost to a remote backend),
// any existing JWT tokens in localStorage will be invalid for the new backend.
// Clear auth state to avoid confusing "logged in but 401" behavior.
const API_BASE_URL_KEY = 'aviatto_api_base_url';
try {
  const prev = localStorage.getItem(API_BASE_URL_KEY);
  const effectiveApiIdentity = DEV_LOCALHOST
    ? _normalizeLocalhostToIPv4(DEV_PROXY_API || ENV_API || 'http://127.0.0.1:8000/api')
    : API_BASE_URL;
  // Treat localhost and 127.0.0.1 as equivalent to avoid clearing tokens on harmless dev URL rewrites.
  const prevNorm = _normalizeLocalhostToIPv4(prev);
  if (prevNorm && prevNorm !== effectiveApiIdentity) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile');
    // Drop persistent HTTP cache so the new backend never sees stale entries
    // captured against a previous environment (e.g. local <-> production).
    _clearPersistentCacheAsync();
  }
  localStorage.setItem(API_BASE_URL_KEY, effectiveApiIdentity);
} catch {
  // Ignore (e.g., SSR or blocked storage)
}

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// CRITICAL: capture the *original* request function before we monkey-
// patch anything. Axios v1's createInstance() builds the instance like:
//
//   const instance = bind(Axios.prototype.request, context);
//   utils.extend(instance, Axios.prototype, context);  // .get/.post/.. are bound to `context`
//   utils.extend(instance, context);                   // .interceptors etc. shared
//
// That means `apiClient.get(url, cfg)` internally does
// `Axios.prototype.get.call(context, ...)` -> `this.request(...)` where
// `this === context`, NOT `apiClient`. So overriding *only*
// `apiClient.request` (as we used to) was a no-op for every service in
// the app: their calls flowed straight through `Axios.prototype.request`
// on `context`, completely bypassing the SWR / dedupe / L0 layers.
//
// To actually intercept, we have to override the method aliases on the
// instance too. Each alias rebuilds the request config the way axios
// would and then funnels through `requestWithSharedCache`.
const rawApiRequest = typeof apiClient.request === 'function'
  ? apiClient.request.bind(apiClient)
  : ((config) => apiClient(config));

apiClient.request = function requestWithSharedCache(config = {}) {
  const requestConfig = _stampRequestSession({ ...config });

  if (!_shouldUseSharedGetCache(requestConfig)) {
    return queuedRawApiRequest(requestConfig).then((response) => {
      _assertRequestSessionUnchanged(requestConfig);
      return response;
    });
  }

  const cacheKey = _buildRequestCacheKey(requestConfig);
  const now = Date.now();
  const ttlMs = Number.isFinite(Number(requestConfig.__cacheTtlMs))
    ? Math.max(0, Number(requestConfig.__cacheTtlMs))
    : DEFAULT_GET_CACHE_TTL_MS;

  const cachedEntry = getResponseCache.get(cacheKey);
  if (cachedEntry && cachedEntry.expiresAt > now) {
    return Promise.resolve().then(() => {
      _assertRequestSessionUnchanged(requestConfig);
      return _cloneResponseLike(cachedEntry.response);
    });
  }
  if (cachedEntry) {
    getResponseCache.delete(cacheKey);
  }

  const inflightEntry = inflightGetRequests.get(cacheKey);
  if (inflightEntry) {
    return inflightEntry;
  }

  const persistentRule = _persistentRuleFor(requestConfig);
  const persistentKey = persistentRule ? _buildPersistentCacheKey(requestConfig) : null;
  const persistentInvalidatedAt = persistentKey
    ? _latestPersistentInvalidationAtForKey(persistentKey)
    : 0;

  // Consult the persistent SWR cache asynchronously. If we have a fresh
  // entry: return synthetic response immediately, no network call. If we
  // have a stale entry: return synthetic response AND fire a background
  // revalidation that updates the persistent store for the next visit.
  if (persistentRule && persistentKey) {
    const persistentPromise = (persistentInvalidatedAt
      ? Promise.resolve(null)
      : readPersistentEntry(persistentKey)
    ).then((entry) => {
      _assertRequestSessionUnchanged(requestConfig);
      const verdict = classifyPersistentEntry(entry);
      if (verdict === 'fresh' || verdict === 'stale') {
        if (verdict === 'stale') {
          _kickPersistentRevalidation(requestConfig, persistentKey, persistentRule);
        }
        const synthetic = _buildSyntheticResponse(requestConfig, entry.data);
        // Mirror into the in-memory dedupe cache so subsequent calls in
        // the same render cycle short-circuit too.
        getResponseCache.set(cacheKey, {
          response: _cloneResponseLike(synthetic),
          expiresAt: Date.now() + Math.min(ttlMs, 1000),
        });
        // Also keep the synchronous mirror current so a remount that
        // calls `getCachedDataSync` returns the same payload that just
        // satisfied this request.
        _syncMirrorSet(persistentKey, entry.data);
        return synthetic;
      }
      // No usable cache; perform a fresh request and persist the result.
      const requestStartedAt = Date.now();
      return queuedRawApiRequest(requestConfig).then((response) => {
        _assertRequestSessionUnchanged(requestConfig);
        if (_latestPersistentInvalidationAtForKey(persistentKey) <= requestStartedAt) {
          try {
            writePersistentEntry(persistentKey, {
              data: response?.data,
              ttlMs: persistentRule.ttlMs,
              gcTimeMs: persistentRule.gcTimeMs || PERSISTENT_CACHE_DEFAULT_GC_MS,
            }).catch(() => { /* noop */ });
          } catch {
            // ignore persistence errors
          }
          // Populate the sync mirror so the *next* navigation can paint
          // this payload before its useEffect even runs.
          _syncMirrorSet(persistentKey, response?.data);
        }
        return response;
      });
    }).then((response) => {
      _assertRequestSessionUnchanged(requestConfig);
      if (ttlMs > 0) {
        getResponseCache.set(cacheKey, {
          response: _cloneResponseLike(response),
          expiresAt: Date.now() + ttlMs,
        });
      }
      inflightGetRequests.delete(cacheKey);
      return response;
    }).catch((error) => {
      inflightGetRequests.delete(cacheKey);
      throw error;
    });

    inflightGetRequests.set(cacheKey, persistentPromise);
    return persistentPromise;
  }

  const requestPromise = queuedRawApiRequest(requestConfig)
    .then((response) => {
      _assertRequestSessionUnchanged(requestConfig);
      if (ttlMs > 0) {
        getResponseCache.set(cacheKey, {
          response: _cloneResponseLike(response),
          expiresAt: Date.now() + ttlMs,
        });
      }
      inflightGetRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      inflightGetRequests.delete(cacheKey);
      throw error;
    });

  inflightGetRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Re-route every method alias through `apiClient.request` so the cache
// layer above actually engages. See the comment block above `rawApiRequest`
// for why this is required - axios v1 binds these to an internal `context`
// whose `request` is the *original* `Axios.prototype.request`, not our
// override. Without this, every service in the app silently bypasses the
// SWR / L0 / dedupe layers.
//
// Implementation mirrors axios's own alias generation in
// node_modules/axios/lib/core/Axios.js (forEachMethodNoData /
// forEachMethodWithData) so behavior is identical to vanilla axios for
// every code path EXCEPT that we go through `apiClient.request` (i.e. our
// override) instead of `Axios.prototype.request` (the bypass).
const _METHODS_NO_DATA = ['delete', 'get', 'head', 'options'];
const _METHODS_WITH_DATA = ['post', 'put', 'patch'];

function _bindAliasNoData(method) {
  return function aliasNoData(url, config) {
    return apiClient.request({
      ...(config || {}),
      method,
      url,
      data: (config || {}).data,
    });
  };
}

function _bindAliasWithData(method, isForm = false) {
  return function aliasWithData(url, data, config) {
    return apiClient.request({
      ...(config || {}),
      method,
      headers: isForm
        ? { 'Content-Type': 'multipart/form-data', ...((config || {}).headers || {}) }
        : ((config || {}).headers || {}),
      url,
      data,
    });
  };
}

_METHODS_NO_DATA.forEach((method) => {
  apiClient[method] = _bindAliasNoData(method);
});

_METHODS_WITH_DATA.forEach((method) => {
  apiClient[method] = _bindAliasWithData(method, false);
  apiClient[`${method}Form`] = _bindAliasWithData(method, true);
});

/**
 * Synchronously read the last-known successful response payload for a
 * GET request, or `undefined` if no cache entry exists for the current
 * user scope.
 *
 * Pages use this to seed `useState` initial values so the first paint
 * after a remount shows real data instead of a loading spinner. The
 * background fetch then revalidates and replaces the data once it
 * resolves (no visible flicker on identical payloads).
 *
 * Conventions:
 *   - Only GET requests on URLs whitelisted in PERSISTENT_CACHE_RULES
 *     are eligible.
 *   - The lookup is scoped to the current user/tenant - no leakage
 *     across sessions.
 *   - Returns the payload exactly as it would arrive in `response.data`,
 *     so callers can use the same property access as their async path.
 */
function getCachedDataSync(input = {}) {
  try {
    const url = String(input.url || '');
    if (!url) return undefined;
    const method = String(input.method || 'get').toLowerCase();
    if (method !== 'get') return undefined;

    const fakeConfig = {
      method,
      url,
      params: input.params || {},
      baseURL: input.baseURL || API_BASE_URL || '',
    };
    if (!_persistentRuleFor(fakeConfig)) return undefined;

    const persistentKey = _buildPersistentCacheKey(fakeConfig);
    if (_latestPersistentInvalidationAtForKey(persistentKey)) return undefined;
    return _syncMirrorGet(persistentKey);
  } catch {
    return undefined;
  }
}

apiClient.getCachedDataSync = getCachedDataSync;

/**
 * Public trigger to re-hydrate the in-memory sync mirror from
 * IndexedDB. Call this after a login completes so the user-scoped cache
 * keys (which weren't yet known on boot) become readable
 * synchronously. Safe to call repeatedly - hydration is idempotent and
 * fire-and-forget.
 */
function rehydrateSyncCache() {
  _hydrateSyncMirror();
}

apiClient.rehydrateSyncCache = rehydrateSyncCache;

/**
 * Print the current state of the SWR cache layers to the console.
 *
 * Available from the browser DevTools as `window.__aviattoCacheDebug()`.
 * Use this to diagnose perceived loading issues:
 *
 *   - `userScope`: the per-user cache scope. If this is `anon|...` it
 *     means the user identity isn't in localStorage yet, which would
 *     prevent cache reuse across reloads.
 *   - `inMemorySize`: how many entries are in the synchronous L0 mirror.
 *   - `localStorageEntries`: list of `{ key, sizeKB, ageMin }` for every
 *     `aviatto:syncMirror:*` entry currently persisted.
 *   - `totalLocalStorageKB`: total localStorage usage by the sync mirror.
 *
 * If `localStorageEntries` is empty after visiting Dashboard / Flight
 * Agenda / Flight Checklist, the L0 layer isn't working - likely
 * because the page never made a successful GET against a whitelisted
 * endpoint, or because the user-scope changed mid-session and old keys
 * are now invisible to the current scope.
 */
function debugCacheState() {
  const userScope = _currentUserScopeForCache();
  const userScopeMarker = `|${userScope}|`;

  const lsEntries = [];
  let totalBytes = 0;
  try {
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i += 1) {
        const lsKey = localStorage.key(i);
        if (!lsKey || !lsKey.startsWith(SYNC_MIRROR_LS_PREFIX)) continue;
        const raw = localStorage.getItem(lsKey) || '';
        const sizeBytes = raw.length * 2;
        totalBytes += sizeBytes;
        const cacheKey = lsKey.slice(SYNC_MIRROR_LS_PREFIX.length);
        const inCurrentScope = cacheKey.includes(userScopeMarker);
        lsEntries.push({
          key: cacheKey,
          sizeKB: Math.round(sizeBytes / 1024),
          inCurrentScope,
        });
      }
    }
  } catch {
    // ignore
  }

  const result = {
    userScope,
    inMemorySize: _syncMirror.size,
    inMemoryKeys: Array.from(_syncMirror.keys()).slice(0, 20),
    localStorageEntries: lsEntries,
    localStorageEntriesInCurrentScope: lsEntries.filter((e) => e.inCurrentScope).length,
    totalLocalStorageKB: Math.round(totalBytes / 1024),
    perEntryCapKB: Math.round(SYNC_MIRROR_MAX_BYTES_PER_ENTRY / 1024),
    maxEntries: SYNC_MIRROR_MAX_ENTRIES,
  };

  // Pretty-print so devs can paste into bug reports.
  // eslint-disable-next-line no-console
  console.log('[aviattoCacheDebug]', result);
  return result;
}

apiClient.debugCacheState = debugCacheState;
try {
  if (typeof window !== 'undefined') {
    window.__aviattoCacheDebug = debugCacheState;
  }
} catch {
  // ignore (SSR or sandboxed env)
}

/**
 * Hydrate the in-memory sync mirror from IndexedDB / localStorage on
 * app boot. This is what makes the very first navigation after a full
 * reload feel instant: without it, the first remount of a cached page
 * would have to await the IndexedDB read before it could short-circuit
 * the loading state.
 *
 * Scoped to the current user (cache keys carry `|<userScope>|`) so
 * unrelated entries from a previous session can't bleed in. Stale and
 * fresh entries are both hydrated; expired ones are skipped because
 * they are guaranteed to be refetched on first use anyway.
 */
function _hydrateSyncMirror() {
  try {
    const userScope = _currentUserScopeForCache();
    const userScopeMarker = `|${userScope}|`;
    getAllPersistentEntries((key) => String(key).includes(userScopeMarker))
      .then((entries) => {
        if (!Array.isArray(entries)) return;
        const now = Date.now();
        for (const { key, entry } of entries) {
          const verdict = classifyPersistentEntry(entry, now);
          if (verdict === 'expired') continue;
          _syncMirrorSet(key, entry?.data);
        }
      })
      .catch(() => { /* hydration is best-effort */ });
  } catch {
    // ignore
  }
}

// Kick off the boot hydration as soon as the module loads. The first
// few useState calls in App.jsx will likely fire before this resolves,
// but every subsequent navigation/remount benefits.
_hydrateSyncMirror();

// Request interceptor - attach JWT token and route local sessions
apiClient.interceptors.request.use(
  async (config) => {
    _stampRequestSession(config);
    _assertRequestSessionUnchanged(config);

    // When logged in via local backend, redirect all API calls there
    // so the JWT (signed with the local SECRET_KEY) is accepted.
    if (_isLocalSession() && !config.__skipLocalRedirect) {
      config.baseURL = LOCAL_BACKEND_API;
    }

    if (!config.__skipAuthHeader) {
      let token = localStorage.getItem('access_token');
      const requestUrl = String(config.url || '');
      if (
        token &&
        !config.__skipAuthRefresh &&
        !_isAuthLifecycleUrl(requestUrl) &&
        _tokenNeedsRefresh(token)
      ) {
        try {
          token = await _ensureFreshAccessToken();
        } catch (refreshError) {
          if (_isSessionChangedError(refreshError)) {
            return Promise.reject(refreshError);
          }
          // Only log out on a definitive auth rejection. On a transient
          // failure keep the (still-valid) session and let the request proceed
          // with the existing token; the response interceptor / transient retry
          // will handle any resulting 401 or network error.
          if (_isDefinitiveAuthFailure(refreshError)) {
            _clearAuthStorage();
            _clearSharedGetCache();
            _redirectToLogin();
            return Promise.reject(refreshError);
          }
          token = localStorage.getItem('access_token') || token;
        }
      }
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        try {
          const decoded = jwtDecode(token);
          const tenantSchema = String(decoded?.tenant_schema || decoded?.schema_name || '').trim();
          if (tenantSchema && !config.headers['X-Aviatto-Tenant-Schema']) {
            config.headers['X-Aviatto-Tenant-Schema'] = tenantSchema;
          }
        } catch {
          // Invalid tokens are handled by the response refresh/logout path.
        }
      }
    }

    // If sending FormData, remove Content-Type header to let browser set it automatically
    // This is necessary for file uploads with multipart/form-data
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and auto-refresh token
apiClient.interceptors.response.use(
  (response) => {
    _assertRequestSessionUnchanged(response?.config || {});
    _assertResponseTenantMatches(response);
    const method = String(response?.config?.method || 'get').toLowerCase();
    if (!RETRYABLE_HTTP_METHODS.has(method)) {
      _clearSharedGetCache();
      _invalidatePersistentForMutation(response?.config || {});
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    try {
      _assertRequestSessionUnchanged(originalRequest || {});
    } catch (sessionError) {
      return Promise.reject(sessionError);
    }

    // Some endpoints (e.g., public billing/checkout) should not trigger global auth refresh or redirect.
    // Caller can set `__skipAuthRefresh: true` in axios config.
    if (originalRequest && originalRequest.__skipAuthRefresh) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    const requestUrl = String(originalRequest?.url || '');
    const isRefreshRequest = requestUrl.includes('/auth/refresh/');
    const isLoginRequest = requestUrl.includes('/auth/login/');

    if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshRequest && !isLoginRequest) {
      originalRequest._retry = true;

      try {
        const nextAccessToken = await _ensureFreshAccessToken();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
        try {
          const decoded = jwtDecode(nextAccessToken);
          const tenantSchema = String(decoded?.tenant_schema || decoded?.schema_name || '').trim();
          if (tenantSchema) {
            originalRequest.headers['X-Aviatto-Tenant-Schema'] = tenantSchema;
          }
        } catch {
          // If decoding fails, retry with Authorization only and let the backend decide.
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        if (_isSessionChangedError(refreshError)) {
          return Promise.reject(refreshError);
        }
        // Definitive rejection (refresh token invalid/expired) ⇒ end the
        // session. Transient failure (network/5xx/timeout) ⇒ keep the session
        // and surface the error so the caller/transient-retry can recover; the
        // refresh token is still valid and the next attempt can succeed.
        if (_isDefinitiveAuthFailure(refreshError)) {
          _clearAuthStorage();
          _clearSharedGetCache();
          _redirectToLogin();
        }
        return Promise.reject(refreshError);
      }
    }

    if (_shouldRetryTransientRequest(error)) {
      const retryCount = Number(originalRequest.__transientRetryCount || 0);
      if (retryCount < MAX_TRANSIENT_RETRIES) {
        originalRequest.__transientRetryCount = retryCount + 1;
        const backoffMs = 600 * (retryCount + 1);
        await _sleep(backoffMs);
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export {
  _buildRequestCacheKey,
  _clearSharedGetCache,
  _clearPersistentCacheAsync as _clearPersistentCache,
  _isLocalSession,
  LOCAL_BACKEND_API,
};
export default apiClient;
